// ANA-R1-03 — the AI-findings tab badge count, scoped to the CURRENT global
// data-point filter (incl. L1/L2 status + reviewState) AND the AI filter
// module's class/confidence narrowing.
//
// The 2026-06-06 walkthrough found the badge reading 8 while the two data
// points matching the filter carried only 2+2 = 4 findings; with an
// unseen-only (L2) reviewState filter it still counted findings on
// already-seen images. Root cause: the badge was the dataset-wide
// `getFindings` `total` — server-computed, narrowed ONLY by `aiClassFilter`
// (SB6-ANA-FILT-07), never by the rest of the global data-point selection.
// Christian's positive control [00:25:58]: *"seven images with 10 receptors
// this would be exactly what I expect"*.
//
// There is no single endpoint that returns "findings under the full 7-axis
// filter" — `getFindings` accepts only `imageId` + `aiClassFilter` (verified
// against `GetFindingsParams`; no contract change is needed or made). So the
// badge is derived CLIENT-SIDE from the two sets the surface already owns,
// exactly the way the Tiles widget's "All images" scope already does
// (TilesWidget `findingsInScope`):
//
//   * `useFilteredDataPoints` → the vis-image ids of the data points matching
//     the current global selection (status/reviewState/component/location/
//     severity + the AI sub-filter folded into `getDataPoints`). This is the
//     L1/L2-and-below image scope.
//   * `useEffectiveFindingSet` (dataset-wide) → the effective AI findings,
//     state-agnostic, narrowed per finding by the SAME committed
//     `aiClassFilter` (ANA-R1-02 / FILT-07). No second filter chain.
//
// Badge = | { f ∈ effective findings : f.imageId ∈ filtered vis-image ids } |.
// Both inputs come from the shared selectors, so the badge updates in the
// same render pass as the List contents (AC3) and can never disagree with the
// "All images" Tiles count.
import { useMemo } from "react";
import { useEffectiveFindingSet } from "./useEffectiveFindingSet.js";
import { useFilteredDataPoints } from "./useFilteredDataPoints.js";

/**
 * Page size for the badge's underlying findings query. The dataset-wide
 * effective set must be COMPLETE for the count to be correct — a truncated
 * page would undercount on finding-heavy DAs (AIF-imported sets can hold
 * thousands). `useFindingsQuery` page-walks + stitches when the requested
 * size exceeds the 200 contract cap (see `fetchAllListPages`), so a large
 * ceiling yields the full set in one stitched response.
 */
const BADGE_FINDINGS_PAGE_SIZE = 5000;

export interface FilteredFindingCount {
  /**
   * Σ findings over data points matching the current global data-point filter
   * AND the AI class/confidence narrowing — the AI-findings tab badge value
   * (AC1). `undefined` while either underlying set is still loading, so the
   * badge can render a placeholder instead of a transient wrong number.
   */
  count: number | undefined;
  /** True while either underlying query is still resolving its first page. */
  isPending: boolean;
}

/**
 * Derive the AI-findings tab badge count for a data acquisition: the number
 * of effective AI findings whose image is in the globally-filtered data-point
 * set. Shares both inputs with the rest of the Analysis surface, so it never
 * issues a parallel filter chain and never goes stale relative to the List.
 */
export function useFilteredFindingCount(
  dataAcquisitionId: string,
): FilteredFindingCount {
  const findingSet = useEffectiveFindingSet(dataAcquisitionId, {
    pageSize: BADGE_FINDINGS_PAGE_SIZE,
  });
  const filtered = useFilteredDataPoints(dataAcquisitionId);

  const findingsQuery = findingSet.query;
  const findingsReady =
    !findingsQuery.isPending && findingsQuery.data !== undefined;
  const dataPointsReady = !filtered.isPending;

  const count = useMemo<number | undefined>(() => {
    if (!findingsReady || !dataPointsReady) return undefined;
    const imageIds = filtered.visibleImageIds;
    let total = 0;
    for (const f of findingSet.findings) {
      if (imageIds.has(f.imageId)) total += 1;
    }
    return total;
  }, [
    findingsReady,
    dataPointsReady,
    filtered.visibleImageIds,
    findingSet.findings,
  ]);

  return { count, isPending: !findingsReady || !dataPointsReady };
}
